import React, { useRef, useState } from "react";
import { fromEvent } from "rxjs";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { get } from "lodash";

import { notify } from "@cayuse/shared-react.components.banner-notification";
import {
    isNonEmptyArray,
    isFunction,
} from "@cayuse/shared-react.utils.type-checks";

import {
    ERROR_STATUS_CODE_410,
    ERROR_UPDATE_REJECTED,
    IS_DEV,
    MAX_RETRY_NUMBER,
    MODIFICATION_DRAFT,
} from "globals/constants";
import {
    getKeyboardFocusableElements,
    notifyError,
    parseHttpErrorBody,
    trapFocusInsideNotification
} from "utils";
import { useDictionary, useRequestChain } from "hooks";
import { formSelectors } from "store/selectors";
import { modificationListSelectors } from "store/selectors";
import {
    setChangedFormKeys,
    updateFormEntity,
    updateForm,
    editForm,
    setFormEditMode,
    checkForm,
    resetForm,
    setFormContentKeys,
    setFormEntityChanged,
} from "store/actions/form";

import { ConflictsModal } from "../ConflictsModal";
import { RetryModal } from "../RetryModal";
import Helper from "../helper";
import { ACTIONS } from "../FormActions/constants";
import { FormEditWarning } from "components/FormResponse/FormEditWarning";

export const FORM_EVENTS = {
    FORM_READY: "Form.ready",
    BEFORE_CHANGE: "Payload.beforeChange",
    AFTER_CHANGE: "Payload.afterChange",
    FOCUS: "Component.focus",
    BLUR: "Component.blur",
    CUSTOM_MANUAL_FOCUS: "custom-manual-focus",
};

const failedFormElements = {
    fields: null
};

export const useFormSave = (args) => {
    const {
        form,
        formType,
        onReload,
        canEdit,
        isModificationChange,
        onFlowableFormSave,
        formSaving,
        dictionary,
        recordRevisionEditMode,
    } = args;

    const formResponseDictionary = useDictionary("common.formResponse");

    const {
        payloadContentType,
        payloadRootContentKey,
        changedVariables,
        isEditMode: editMode,
    } = useSelector(formSelectors.entity);

    const {
        latestModificationNumber,
        items: modificationList
    } = useSelector(modificationListSelectors.content);

    const isModification = useSelector(
        modificationListSelectors.isModification
    );

    const retriesRef = useRef(0);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [actionBtnClicked, setActionBtnClicked] = useState(false);
    const [hasConflict, setHasConflict] = useState(false);
    const [lastConflictField, setLastConflictField] = useState(null);

    const formChecking = useRequestChain();

    const saveForm = async (payload) => {
        const readyForSave = payloadContentType === "subawardContent" ? latestModificationNumber != null : true;
        if (!readyForSave) {
            // For subawards, skip saveForm until latestModificationNumber is set
            return;
        }
        const payloadContent = isModification
            ? payload[payloadContentType]
            : get(payload, `root.${payloadContentType}`, {});

        const previousPayloadContent =
            changedVariables[payloadContentType] ??
            get(changedVariables, `root.${payloadContentType}`, {});

        Helper.normalizePayload(payload, form.formElements, formType);

        const hasBothRootAndDirectContent = payload.hasOwnProperty("root")
            && payload.root.hasOwnProperty(payloadContentType)
            && payload.hasOwnProperty(payloadContentType);

        if (recordRevisionEditMode) {
            const isLatestAwardModification = payload.submissionType === "award"
                && !payload.createAwardModificationTaskId;
            if (hasBothRootAndDirectContent && (!isModification || isLatestAwardModification)) {
                delete payload[payloadContentType];
            }
            //todo: check if isModificationChange
            const formattedPayload = Helper.formatPayload(payload);
            const response = await updateForm(formattedPayload, onReload);
            if (isFunction(onFlowableFormSave)) {
                onFlowableFormSave();
            }
            return response;
        } else {
            if (isModification) {
                const currentModification = (modificationList ?? []).find(
                    (item) =>
                        item.modificationNumber === latestModificationNumber
                );

                if (!currentModification || currentModification.caseInstanceId !== payload.caseInstanceId) {
                    return;
                }
                const isDraft = currentModification.modificationStatus === MODIFICATION_DRAFT;
                if (!isDraft && hasBothRootAndDirectContent) {
                    notifyError(dictionary.saveErrorWithRefresh
                        || "An error occurred and your changes have not been saved. Please reload the page and try again.");

                    return;
                }
            } else if (Helper.isSamePayload(payloadContent, previousPayloadContent)) {
                return;
            }
            return saveFormChanges(payload);
        }
    };

    const saveFormChanges = (payload) => {
        if (canEdit && !isModificationChange) {
            const formattedPayload = Helper.formatPayload(payload);

            setFormEntityChanged(true);

            formSaving.addRequest(
                "save-form",
                () => {
                    return updateFormEntity(
                        formattedPayload,
                        formSaving.clearByName.bind(null, "save-form")
                    )
                        .then(() => {
                            if (isFunction(onFlowableFormSave)) {
                                onFlowableFormSave();
                            }

                            if (!hasConflict) {
                                retriesRef.current = 0;
                                setLastConflictField(null);
                            }

                            setHasConflict(false);
                            setFormEntityChanged(false);
                        })
                        .catch(async (error) => {
                            if (error.conflictingFields) {
                                if (
                                    Array.isArray(error.conflictingFields) &&
                                    Array.isArray(lastConflictField) &&
                                    !error.conflictingFields.every((field, index) =>
                                        field.fieldName === lastConflictField[index]?.fieldName
                                    )
                                ) {
                                    retriesRef.current = 0;
                                }

                                setLastConflictField(error.conflictingFields);
                                setHasConflict(true);
                                if (retriesRef.current > MAX_RETRY_NUMBER) {
                                    notify(
                                        <RetryModal
                                            fields={error.conflictingFields}
                                        />,
                                        { type: "warning", autoClose: false }
                                    );
                                } else {
                                    const lastActiveElement = document.activeElement;
                                    retriesRef.current += 1;
                                    // use toast instead of notify to show new conflict errors
                                    toast(<ConflictsModal fields={error.conflictingFields} />, {
                                        type: "warning",
                                        autoClose: false,
                                        onOpen: () => {
                                            trapFocusInsideNotification();
                                        },
                                        onClose: () => {
                                            setTimeout(() => {
                                                lastActiveElement.focus();
                                            }, 0);
                                        }
                                    });
                                }
                            } else if (
                                isModification &&
                                error.response.status === ERROR_STATUS_CODE_410
                            ) {
                                notifyError(
                                    IS_DEV
                                        ? error.message
                                        : formResponseDictionary.modificationUnavailable,
                                    error
                                );
                            } else if (!payload.isUploadingAttachment) {
                                const errorObject = await parseHttpErrorBody(
                                    error.cause
                                );

                                if (
                                    errorObject?.error === ERROR_UPDATE_REJECTED
                                ) {
                                    notifyError(
                                        IS_DEV
                                            ? error.message
                                            : formResponseDictionary.recordNotEditable,
                                        error
                                    );
                                } else {
                                    notifyError(
                                        IS_DEV
                                            ? error.message
                                            : dictionary.saveError,
                                        error
                                    );
                                }
                            }
                        });
                },
                formattedPayload
            );
        }
    };

    const showWarning = (failedFormElementsMap) => {
        const rules =
            failedFormElements.fields && isNonEmptyArray(failedFormElements.fields.RULES)
                ? failedFormElementsMap.RULES.filter(
                    (item) =>
                        !Helper.containsField(
                            failedFormElements.fields.RULES,
                            item
                        )
                )
                : failedFormElementsMap.RULES;

        const accesses =
            failedFormElements.fields && isNonEmptyArray(failedFormElements.fields.ACCESSES)
                ? failedFormElementsMap.ACCESSES.filter(
                    (item) =>
                        !Helper.containsField(
                            failedFormElements.fields.ACCESSES,
                            item
                        )
                )
                : failedFormElementsMap.ACCESSES;

        if (isNonEmptyArray(rules) || isNonEmptyArray(accesses)) {
            formSaving.clearByName("check-form");
            const lastActiveElement = document.activeElement;

            toast(
                <FormEditWarning rules={rules} accesses={accesses} />,
                {
                    type: "warning",
                    autoClose: false,
                    onOpen: () => {
                        trapFocusInsideNotification();
                    },
                    onClose: () => {
                        setTimeout(() => {
                            lastActiveElement.focus();
                        }, 0);
                    }
                }
            );
        }

        failedFormElements.fields = failedFormElementsMap;
    }

    const checkFormRules = async (payload) => {
        if (editMode) {
            const payloadContent = isModification
                ? payload[payloadContentType]
                : get(payload, `root.${payloadContentType}`, {});
            const previousPayloadContent =
                changedVariables[payloadContentType] ??
                get(changedVariables, `root.${payloadContentType}`, {});
            if (Helper.isSamePayload(payloadContent, previousPayloadContent)) {
                return;
            }
            setDirty(true);
            Helper.normalizePayload(payload, form.formElements, formType);
            const formattedPayload = Helper.formatPayload(payload);
            formChecking.addRequest(
                "check-form",
                () => {
                    return checkForm(formattedPayload, onReload).then(response => {
                        if (
                            response &&
                            (isNonEmptyArray(response.failedFormElementsMap.RULES) ||
                                isNonEmptyArray(response.failedFormElementsMap.ACCESSES))
                        ) {
                            showWarning(response.failedFormElementsMap);
                        }
                    });
                });
        }
    };

    const focusedFieldKey = useRef("");
    const focusedFieldPath = useRef("");

    const onEvent = (eventName, config, formState, api) => {
        switch (eventName) {
            /* istanbul ignore next */
            case FORM_EVENTS.FORM_READY: {
                const button = document.getElementsByClassName(
                    "flw__tabs__tab-item__button"
                );
                const formField = button && fromEvent(button, "keydown");

                if (button.length > 0 && formField) {
                    formField.subscribe((event) => {
                        /* istanbul ignore next */
                        if (event.key === "Enter") {
                            event.target.click();

                            const formSection = document.getElementsByClassName(
                                "formSection__content-wrapper"
                            );
                            const elements = getKeyboardFocusableElements(
                                formSection.item(0)
                            );

                            return elements[0]?.focus();
                        }
                    });
                }

                return;
            }
            case FORM_EVENTS.BEFORE_CHANGE: {
                // we can set path in 'focus' event but if config is empty - it will not work.
                focusedFieldPath.current = formState.$path;

                return;
            }
            case FORM_EVENTS.AFTER_CHANGE: {
                if (recordRevisionEditMode) {
                    //Check if the changed form element is involved in form rules
                    checkFormRules(formState);
                    return;
                }
                // get last part of the string.
                // string example: "root.proposalContent...sponsor"
                const currentFieldKey = focusedFieldPath.current
                    ?.split(".")
                    ?.slice(-1)[0];

                // set key if value changed and field is focusedFieldKey
                if (focusedFieldKey.current === currentFieldKey) {
                    setChangedFormKeys(currentFieldKey);
                }

                saveForm(formState);

                return;
            }
            case FORM_EVENTS.CUSTOM_MANUAL_FOCUS:
            case FORM_EVENTS.FOCUS: {
                if (!config) {
                    const error = "Config is not provided";
                    /* istanbul ignore next */
                    console.error(error, config);
                    return error;
                }
                if (!config.$path) {
                    const error = "$path is not specified inside the config";
                    /* istanbul ignore next */
                    console.error(error, config);
                    return error;
                }
                // get first part of the string.
                // string may contain a deep focusedFieldPath: "field-key.0.0.some-deep-key"
                focusedFieldKey.current = config.$path?.split(".")[0];

                return;
            }
            case FORM_EVENTS.BLUR: {
                focusedFieldKey.current = "";
                focusedFieldPath.current = "";

                return;
            }
        }
    };

    const onActionTriggered = async (action) => {
        setActionBtnClicked(true);

        switch (action) {
            case ACTIONS.EDIT:
                await onEditForm();
                break;
            case ACTIONS.CANCEL:
                await editForm(false, onReload);
                setFormEditMode(false);
                setDirty(false);
                resetForm();
                setFormContentKeys({ payloadRootContentKey, payloadContentType });

                break;
            case ACTIONS.SAVE:
                setSaving(true);
                const saveResponse = await saveForm(
                    Helper.formRef.getPayload()
                );
                setFormEditMode(false);
                setDirty(false);
                setSaving(false);

                if (saveResponse) {
                    notify(
                        dictionary.successSaveMsg ??
                        "Your changes have been saved",
                        {
                            type: "success",
                            autoClose: true,
                        }
                    );
                }
                break;
        }

        setActionBtnClicked(false);
    };

    const onEditForm = async () => {
        const response = await editForm(true, onReload);
        if (response) {
            setFormEditMode(true);
        }
    };

    return {
        onEvent,
        onActionTriggered,
        onEditForm,
        dirty,
        saving,
        actionBtnClicked,
    };
};
